import {MAX_UINT32_ELEMS, pixelBufferType} from "_src/engine/constants";
import minmaxUtils from "_src/engine/utility/minmax";
import { colorUtils } from "_src/utils";
import {colorType, objectType} from "_src/constants";
import appState from "_src/app_state";
import config from "_src/config";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import output from "_src/engine/output";
import GroupableWrapper from "_src/groupable_wrapper";

const _minmaxTmp = new Array(6);
const _minmaxTmp2 = new Array(6);

export default class ObjectWrapper extends GroupableWrapper {

  constructor(params) {
    super(params);
    if (params.type !== undefined) this.type = params.type;
    this.model = params.model !== undefined ? params.model : null;
    //NOTE: it's a hack for the 6th format
    this.shouldUseTexture = params.usetexture !== undefined ? params.usetexture : false;

    this.frames = [];
    this.geometryLevel = null;

    this.stateFlags = 0;

    this.hasKeyFrames = false;
    this._framesTemporaryTexture = null;
  }

  getLockState() {
    return false;
    //feature is disabled before cloud implementation
    //return this._selfPropertyGetter("locked");
  }

  setLockState(locked) {
    this._selfPropertySetter("locked", locked);
  }

  getScannerType() {
    return this._selfPropertyGetter("scannertype");
  }

  getDeviceSerial() {
    return this._selfPropertyGetter("deviceserial");
  }

  copyLocalMinmax(minmax) {
    minmaxUtils.copy(this.getLocalMinmax(), minmax);
  }

  copyWorldMinmax(minmax) {
    minmaxUtils.copy(this.getWorldMinmax(), minmax);
  }

  getWorldMinmax() {
    return this.model.minmaxWorld;
  }

  getLocalMinmax() {
    return this.model.minmaxLocal;
  }

  expandWorldMinMax(minmax) {
    minmaxUtils.expandMinmax(this.getWorldMinmax(), minmax);
  }

  expandLocalMinMax(minmax) {
    minmaxUtils.expandMinmax(this.getLocalMinmax(), minmax);
  }

  loadData(property, level = 0) {

    this.geometryLevel = level;
    this.model.setSelectionEnabled(false);
    this.loaded = false;

    return this._loadFrames(property, level);

  }

  setState(mask, value) {
    this.stateFlags = value ? this.stateFlags | mask : this.stateFlags & (MAX_UINT32_ELEMS ^ mask);
  }

  getState(mask) {
    return Boolean(this.stateFlags & mask);
  }

  _loadFrames(properties, level) {

    const promises = [];

    for (const frame of this.frames) {
      const frameProperty = properties.find(property => property.getValue("id") === frame.getID());
      if (!frameProperty) {
        output.error(output.DEVELOPERS_ERROR, "Can't find frame " + frame.getID() + " in project sjson");
        continue;
      }
      promises.push(this._processFrameLoading(frame, frameProperty, level));
    }

    return Promise.all(promises);

  }

  updateFramesTemporaryTextures() {

    if (!this.hasTextures()) {
      return;
    }

    this._initFramesTemporaryTexture();
    for (const frame of this.frames) {
      if (frame.textureList.length == 0) {
        this.setFrameTemporaryTexture(frame);
      }
    }

  }

  _initFramesTemporaryTexture() {
    const textureColor = this.type == objectType.FUSION ?
        colorUtils.linToSRGB(this.getColor(), new Array(3)) : this.getColor();
    const scanColorTextureBuffer = new Uint8Array([...textureColor.map(colorUtils.floatToByte), 255]);

    this._framesTemporaryTexture = bufferFactory.create(pixelBufferType.T_RGBA,
      {
        width: 1,
        height: 1,
        immutable: true,
      });
    this._framesTemporaryTexture.initMipMapLevelBuffer(0, scanColorTextureBuffer);
  }

  setFrameTemporaryTexture(frame) {
    if (!this._framesTemporaryTexture) {
      this._initFramesTemporaryTexture();
    }

    const currentTexture = this.model.textures[0];
    if (currentTexture && (currentTexture !== this._framesTemporaryTexture)) {
      currentTexture.cleanup();
    }

    frame.model.textures[0] = this._framesTemporaryTexture;
    frame.model.properties.enableLinearColorSpace = true;
  }

  _unsetFrameTemporaryTexture(frame) {
    if (frame.model.textures[0] === this._framesTemporaryTexture) {
      frame.model.textures.length = 0;
    }
  }

  async _processFrameLoading(frame, property, level) {
    frame.cacheID = this.getID() + "_" + this.type + "_" + this.geometryLevel + "_" + frame.getID();
    const isGeometryLoading = frame.checkIsGeometryLoading();
    const promises = [];

    if (frame.shouldBeLoaded) {

      if (this.hasTextures() && !frame.texturesLoaded && !frame.texturePromise) {
        this.setFrameTemporaryTexture(frame);
        if (config.viewOnlyMode) {
          await frame.loadTexture();
        } else {
          promises.push(frame.loadTexture());
        }
        this.updateTextureUsage();
      }

      if (!frame.loaded) {

        if (isGeometryLoading) {
          promises.push(frame.getGeometryLoadingPromise());
        } else {
          promises.push(frame.loadGeometry(property, this.id, level));
        }

      }

    }

    await Promise.all(promises);
  }

  checkNeedsLoadFrames() {
    for (const frame of this.frames) {
      if(frame.shouldBeLoaded && !frame.loaded) {
        return true;
      }
    }
    return false;
  }

  unloadMarkedFrames() {
    for (const frame of this.frames) {
      if (!frame.shouldBeLoaded) {
        if (frame.checkIsGeometryLoading()) {
          frame.abortLoading(this);
        } else if (frame.loaded) {
          this._unsetFrameTemporaryTexture(frame);
          frame.unloadData();
          frame.unloadTextures();
        }
      }
    }
  }

  async unloadData() {
    for (let i = 0; i < this.frames.length; ++i) {

      const frame = this.frames[i];
      frame.abortLoading(this);
      await frame.unloadData();
      frame.unloadTextures();
      frame.shouldBeLoaded = false;

    }
    // NOTE: do not call this before frame loop cleanup because of
    // fusion's frame has the same model as itself
    this.model.cleanup();
    this.stateFlags = 0;

    this.loaded = false;

  }

  addFrame(frame) {
    this.frames.push(frame);

    this._recalculateHasKeyFrames();

    this.expandWorldMinMax(frame.getWorldMinmax());
    this.expandLocalMinMax(frame.getLocalMinmax());
    frame.setVisibility(this.getVisibility());
  }

  removeFrame(frameToDelete) {
    frameToDelete.abortLoading(this);
    frameToDelete.unloadData();
    frameToDelete.unloadTextures();
    frameToDelete.cleanup();

    this.frames.splice(this.frames.indexOf(frameToDelete), 1);

    const localMinmax = minmaxUtils.reset(_minmaxTmp);
    const worldMinmax = minmaxUtils.reset(_minmaxTmp2);
    for (const frame of this.frames) {
      const frameMinmax = minmaxUtils.recomputedFromRotated([], frame.getLocalMinmax(), frame.model.localMatrix);
      minmaxUtils.expandMinmax(localMinmax, frameMinmax);
      minmaxUtils.expandMinmax(worldMinmax, frame.getWorldMinmax());
    }

    minmaxUtils.copy(this.getLocalMinmax(), localMinmax);
    minmaxUtils.copy(this.getWorldMinmax(), worldMinmax);

    this._recalculateHasKeyFrames();
  }

  getAllLoadedDrawables() {
    const drawables = [];

    for (const frame of this.frames) {
      if (frame.loaded) {
        drawables.push(frame.model);
      }
    }

    return drawables;
  }

  copyTranslation(trans) {
    this.model.copyTranslation(trans);
  }

  copyRotation(quat) {
    this.model.copyRotation(quat);
  }

  copyScale(scale) {
    this.model.copyScale(scale);
  }

  setVisibility(visible) {
    super.setVisibility(visible);
    for (let i = 0; i < this.frames.length; ++i) {
      this.frames[i].setVisibility(visible);
    }

    this.model.setVisibility(visible);
  }

  hasTextures() {

    if (this.frames.length === 0) {
      return this.shouldUseTexture;
    }

    for (const frame of this.frames) {
      if (frame.textureList.length) {
        return true;
      }
    }

    return false;

  }

  isDistanceMapAttached() {
    if (this.frames.length === 0) {
      return false;
    }

    let has = false;
    for (const chunk of this.frames[0].model.chunks) {
      if (chunk.geometry && chunk.geometry.hasLayoutItem("a_distance")) {
        has = true;
        break;
      }
    }

    return has;
  }

  hasVertexColor() {
    if (this.frames.length === 0) {
      return false;
    }

    let has = false;
    for (const chunk of this.frames[0].model.chunks) {
      if (chunk.geometry && chunk.geometry.hasLayoutItem("a_color")) {
        has = true;
        break;
      }
    }

    return has;
  }

  resolveColorMode(colorRequested) {
    if (this.isDistanceMapAttached()) return colorType.DISTANCE_MAP;
    if (!colorRequested) return colorType.UNIFORM;
    if (this.hasVertexColor()) return colorType.BUFFER;
    if (!this.hasTextures()) return colorType.UNIFORM;
    if (this.type === objectType.FUSION) return colorType.TEXTURE_UV;
    if (this.type === objectType.SCAN) return colorType.TEXTURE_PROJECTION;

    return colorType.UNIFORM;
  }

  applyObjectColorToFrames() {

    const color = this.getColor();
    for (let i = 0; i < this.frames.length; ++i) {
      this.frames[i].model.setDiffuseColor(...color);
    }

  }

  applyTemporaryColorToFrames(color) {
    for (let i = 0; i < this.frames.length; ++i) {
      this.frames[i].model.setDiffuseColor(...color);
    }
  }

  applyUniqueColorToFrames() {

    for (let i = 0; i < this.frames.length; ++i) {

      const frame = this.frames[i];
      const model = frame.model;
      model.setDiffuseColor(...frame.getColor());

    }

  }

  setColor(r, g, b) {
    super.setColor(r, g, b);
    for (const frame of this.frames) {
      frame.model.setDiffuseColor(r, g, b);
    }
  }

  setOpacity(opacity) {
    this.model.setOpacity(opacity);
    for (let child of this.model.getChildren()) {
      child.setOpacity(opacity);
    }
  }

  move(matrix, withPropertyUpdate = false) {
    this.model.setMatrix(matrix);
    if (withPropertyUpdate) this._selfPropertySetter("transform", matrix);
  }

  applyMaxErrorColorToFrames() {

    const colorTmp = new Array(3);

    const scanner = this.getScannerType();

    if (this.type === objectType.SCAN) {

      for (const frame of this.frames) {
        const [r, g, b] = colorUtils.getErrorColor(frame.getRegError(), scanner, colorTmp);
        frame.model.setDiffuseColor(r, g, b);
      }

    } else {
      for (const frame of this.frames) {
        const {r, g, b} = config.maxErrorFusionColor;
        frame.model.setDiffuseColor(r, g, b);
      }
    }

  }

  hideNonTexturedFrames() {

    const frames = this.frames;

    for (let i = 0; i < frames.length; ++i) {

      const frame = frames[i];

      if (!frame.textureList.length) {
        frame.setVisibility(false);
      }

    }

  }

  hideAllFrames() {
    for (const frame of this.frames) {
      frame.setVisibility(false);
    }
  }

  showNonTexturedFrames() {
    for (const frame of this.frames) {
      if (!frame.textureList.length) {
        frame.setVisibility(true);
      }
    }
  }

  showKeyFrames() {
    for (const frame of this.frames) {
      if (frame.isKeyFrame()) {
        frame.setVisibility(true);
      }
    }
  }

  recalcWorldMinmax() {

    this.model.updateMatrix();

    const frames = this.frames;
    const promises = [];

    for (let frame of frames) {
      if (frame.loaded) {
        promises.push(frame.calculateBoundings());
      }
    }

    return Promise.all(promises).then((worldMinmaxs) => {

      if (worldMinmaxs.length === 0) {
        return this.getWorldMinmax();
      }

      if (frames.length === 0) {
        minmaxUtils.zero(this.getWorldMinmax());
        return this.getWorldMinmax();
      }

      const minmax = minmaxUtils.reset(_minmaxTmp);

      for (let worldMinmax of worldMinmaxs) {
        minmaxUtils.expandMinmax(minmax, worldMinmax);
      }

      minmaxUtils.copy(this.getWorldMinmax(), minmax);

      return minmax;

    });

  }

  _recalculateHasKeyFrames() {

    this.hasKeyFrames = false;

    if (this.type === objectType.FUSION) {
      this.hasKeyFrames = true;
      return this.hasKeyFrames;
    }

    for (const frame of this.frames) {
      if (frame.isKeyFrame()) {
        this.hasKeyFrames = true;
        return this.hasKeyFrames;
      }
    }

    return this.hasKeyFrames;

  }

  updateTextureUsage(textureRequested = appState.getStateValue("colorMode") === appState.CM_TEXTURE) {
    const colorMode = this.resolveColorMode(textureRequested);
    this.model.setColorMode(colorMode);

    if (this.type === objectType.SURFELS) {
      const textureUsed = colorMode !== colorType.UNIFORM;
      for (let frame of this.frames) {
        const reflectivity = textureUsed ? config.textureReflectivity : config.diffuseReflectivity;
        frame.model.setReflectivity(reflectivity);
      }
    } else {
      this.updateColorSpace(textureRequested);
    }
  }

  updateColorSpace(textureRequested) {
    const textureUsed = textureRequested && this.hasTextures();

    for (const frame of this.frames) {
      const frameTextureUsed = textureUsed && frame.hasTextures();
      const reflectivity = frameTextureUsed ? config.textureReflectivity : config.diffuseReflectivity;

      if (frameTextureUsed && !frame.texturesLoaded) {
        continue;
      }

      frame.model.setReflectivity(reflectivity);

      frame.model.properties.enableLambertDiffuse = frameTextureUsed;
      frame.model.properties.enableColorCorrection = frameTextureUsed;
    }
  }

  cleanup() {
    for (let frame of this.frames) {
      this._unsetFrameTemporaryTexture(frame);
      frame.cleanup();
    }

    if (this._framesTemporaryTexture) {
      this._framesTemporaryTexture.cleanup();
      this._framesTemporaryTexture = null;
    }
  }

}
